package fr.frux.web

import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.window.ComposeViewport
import fr.frux.App
import fr.frux.core.common.crash.initializeWebCrashReporting
import fr.frux.core.common.logging.Logger
import fr.frux.core.common.logging.webLogLevel
import fr.frux.core.maps.MapAttribution
import fr.frux.core.maps.initializeMapStyleUrl
import fr.frux.core.maps.initializeWebMapRenderer
import fr.frux.resources.FallbackResourceBytesSource
import fr.frux.resources.RetryingResourceReader
import fr.frux.resources.asBytesSource
import fr.frux.resources.asResourceReader
import fr.frux.shared.di.createWebAppComponent
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.InternalResourceApi
import org.jetbrains.compose.resources.LocalResourceReader
import org.jetbrains.compose.resources.configureWebResources
import org.jetbrains.skiko.wasm.onWasmReady

/**
 * Style par defaut (#519) : les tuiles de Frux, memes pour tous les environnements et sans cle.
 * L'hote doit rester celui que la CSP de `webApp/deploy/_headers` autorise.
 */
private const val DEFAULT_STYLE_URL = "https://tiles.frux-app.fr/style.json"

/**
 * Worker de MapLibre GL JS, servi same-origin (copié dans les ressources par la tâche Gradle
 * `copyMapLibreWorker`). Le défaut de la bibliothèque pointe sur jsDelivr, que la CSP interdit
 * (ADR 0022). Une URL same-origin est utilisée telle quelle par l'amont, sans blob intermédiaire.
 *
 * Chemin absolu, donc valable seulement si la webApp est déployée à la racine de son origine, ce
 * qui est le cas d'`app.frux-app.fr`. Sous un sous-chemin, il faudrait le préfixer comme le fait
 * déjà `configureWebResources` plus bas.
 */
private const val MAP_WORKER_URL = "/maplibre-gl-worker.mjs"

/**
 * URL de style posee par l'hebergeur dans la page (`window.fruxMapStyleUrl`), pour changer de
 * fournisseur de tuiles sans reconstruire le bundle.
 */
private fun injectedMapStyleUrl(): String? = (window.asDynamic().fruxMapStyleUrl as? String)?.takeIf { it.isNotBlank() }

/** Attribution du style par defaut. */
private val DEFAULT_ATTRIBUTION =
    MapAttribution(label = "Protomaps © OpenStreetMap", url = "https://www.openstreetmap.org/copyright")

/**
 * Attribution posee avec l'URL de style (`window.fruxMapAttribution = { label, url }`).
 *
 * Elle voyage avec l'URL et non avec le code : changer de fournisseur de tuiles sans changer la
 * mention afficherait un credit faux, ce qui viole ses conditions d'utilisation.
 */
private fun injectedMapAttribution(): MapAttribution? {
    val raw = window.asDynamic().fruxMapAttribution ?: return null
    val label = (raw.label as? String)?.takeIf { it.isNotBlank() } ?: return null
    val url = (raw.url as? String)?.takeIf { it.isNotBlank() } ?: return null
    return MapAttribution(label = label, url = url)
}

@OptIn(ExperimentalComposeUiApi::class, ExperimentalResourceApi::class, InternalResourceApi::class)
fun main() {
    // Posé par webgl-check.js (#597) : sans WebGL 2, Skiko planterait au montage et Sentry
    // remonterait un robot sans GPU. La page affiche déjà l'explication, on ne démarre rien.
    if (window.asDynamic().fruxWebglUnavailable == true) return
    // Premier geste, avant tout log (#481) : une page publiee n'ecrit rien sous ERROR dans la
    // console. Le niveau vient d'app-config.js, comme l'URL de l'API.
    Logger.minLevel =
        webLogLevel(
            configured = window.asDynamic().fruxLogLevel as? String,
            hostname = window.location.hostname,
        )
    // Juste après le niveau de log et avant tout le reste (#482) : un échec de configuration ou
    // de construction du graphe doit déjà partir vers Sentry. Inerte sans fruxSentryDsn.
    initializeWebCrashReporting(app = "frux-web")
    // Les composeResources se chargent par defaut en chemin relatif : sous une URL profonde
    // (/arbre/42, #299) elles se resoudraient sous /arbre/ et 404. Base absolue obligatoire.
    configureWebResources {
        resourcePathMapping { path -> "/$path" }
    }
    val styleUrl = injectedMapStyleUrl()
    val attribution = injectedMapAttribution()
    val (resolvedStyleUrl, resolvedAttribution) =
        when {
            styleUrl != null && attribution != null -> {
                styleUrl to attribution
            }

            styleUrl == null -> {
                DEFAULT_STYLE_URL to DEFAULT_ATTRIBUTION
            }

            else -> {
                // Un style pose sans attribution afficherait le credit par defaut sur les tuiles
                // d'une autre source : un faux credit viole ses conditions d'utilisation. On
                // refuse la moitie du repli et on retombe entierement sur le defaut (URL + credit).
                console.error(
                    "fruxMapStyleUrl est defini sans fruxMapAttribution : repli complet sur le " +
                        "style par defaut pour eviter d'afficher un credit incorrect.",
                )
                DEFAULT_STYLE_URL to DEFAULT_ATTRIBUTION
            }
        }
    initializeMapStyleUrl(styleUrl = resolvedStyleUrl, attribution = resolvedAttribution)
    val component = createWebAppComponent()
    onWasmReady {
        // Avant que Compose ne construise son moteur de rendu (cf. initializeWebMapRenderer).
        initializeWebMapRenderer(workerUrl = MAP_WORKER_URL)
        ComposeViewport(document.body!!) {
            // Les fontes et images se chargent par fetch : une coupure du réseau mobile pendant
            // la lecture laissait l'écran sans fonte, sans nouvel essai (#619). Le lecteur par
            // défaut est enveloppé, pas remplacé : les chemins restent ceux de resourcePathMapping.
            // Et quand le navigateur refuse CacheStorage (navigation privée, #673), la ressource
            // est relue par un fetch direct au lieu de laisser l'app sur le pré-splash.
            val defaultReader = LocalResourceReader.current.asBytesSource()
            val resilientReader =
                FallbackResourceBytesSource(
                    primary = RetryingResourceReader(defaultReader),
                    fallback = FetchResourceBytesSource(uriOf = defaultReader::getUri),
                ).asResourceReader()
            CompositionLocalProvider(LocalResourceReader provides resilientReader) {
                App(component)
            }
            LaunchedEffect(Unit) {
                // Attendre la prochaine frame Skiko avant de retirer le div crème du pré-splash
                // HTML : sans ça, le remove() peut s'exécuter avant le premier paint Compose et
                // laisser apparaître un flash blanc d'une frame (#202).
                withFrameNanos {}
                document.getElementById("frux-splash")?.remove()
            }
        }
    }
}
